import {Component, EventEmitter, Injector, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {BaseComponent} from '../../base/base.component';
import {Company} from '../../../models/company';
import {Service} from '../../../models/service';
import {StripeInvoice} from '../../../models/stripe-invoice';
import {ServiceService} from '../../../services/service/service.service';
import {PaymentService} from '../../../services/payment/payment.service';
import {TRANSACTION_TARIFF_TIERS, TransactionTariffTier} from '../../../constants/transaction-tariff-tiers';
import {
    companyHasPlan,
    getCompanySubscriptionMeta,
} from '../shared/company-subscription';

@Component({
    selector: 'app-companies-settings-subscription',
    templateUrl: './companies-settings-subscription.component.html',
    styleUrls: ['./companies-settings-subscription.component.scss'],
})
export class CompaniesSettingsSubscriptionComponent extends BaseComponent implements OnChanges {
    @Input() company: Company = new Company();
    @Input() companyRaw: Record<string, unknown> | null = null;
    @Input() companyId = '';
    @Input() userIsSuperAdmin = false;
    @Input() canEditPaymentMethod = true;
    @Input() currentInvoice: StripeInvoice | null = null;
    @Input() currentCardDetails: any = null;

    @Output() reload = new EventEmitter<void>();
    @Output() updatePayment = new EventEmitter<void>();

    readonly defaultTransactionTariffTiers: TransactionTariffTier[] = TRANSACTION_TARIFF_TIERS;

    services: Service[] = [];
    servicesSelected: Service[] = [];
    billingPeriod: 'monthly' | 'yearly' = 'monthly';
    changingPlan = false;
    showStripeCheckout = false;
    retryLoading = false;

    constructor(
        public serviceInjector: Injector,
        private serviceService: ServiceService,
        private paymentService: PaymentService,
    ) {
        super(serviceInjector);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['company'] && this.company?.billedPeriod === 'yearly') {
            this.billingPeriod = 'yearly';
        } else if (changes['company'] && this.company?.billedPeriod === 'monthly') {
            this.billingPeriod = 'monthly';
        }
    }

    get hasPlan(): boolean {
        return companyHasPlan(this.company);
    }

    get subscriptionMeta() {
        return getCompanySubscriptionMeta(this.company, {
            invoice: this.currentInvoice,
            raw: this.companyRaw,
        });
    }

    get canRetryPayment(): boolean {
        return this.userIsSuperAdmin && this.hasPlan && (
            this.subscriptionMeta.key === 'paymentFailed'
            || this.subscriptionMeta.key === 'pendingPayment'
            || this.subscriptionMeta.key === 'pastDue'
        );
    }

    get checkoutEmail(): string {
        return (this.company.legalEmail || this.company.companyEmail || '').trim();
    }

    get replaceExistingPlan(): boolean {
        return this.hasPlan;
    }

    get stripePeriodEndLabel(): string {
        const unix = this.company.stripeSubscriptionCurrentPeriodEnd
            || Number(this.companyRaw?.['stripeSubscriptionCurrentPeriodEnd'] || 0);
        if (!unix) {
            return '';
        }
        const date = new Date(unix * 1000);
        if (Number.isNaN(date.getTime())) {
            return '';
        }
        return date.toLocaleDateString('it-IT');
    }

    async startChangePlan(): Promise<void> {
        this.changingPlan = true;
        this.showStripeCheckout = false;
        this.servicesSelected = [];
        const raw = await this.serviceService.getAllServices(false);
        this.services = (raw || []).map((item) => new Service(item));
    }

    isServiceHidden(service: Service): boolean {
        return service.visible === false || service.visible === 0;
    }

    isCurrentCompanyPlan(service: Service): boolean {
        return !!service.id && service.id === this.company.service?.id;
    }

    cancelChangePlan(): void {
        this.changingPlan = false;
        this.showStripeCheckout = false;
        this.servicesSelected = [];
    }

    selectPlan(service: Service): void {
        const alreadySelected = this.setCheckbox(service);
        this.servicesSelected = alreadySelected ? [] : [service];
    }

    setCheckbox(serviceCheckbox: Service): boolean {
        return !!this.servicesSelected.find((service: Service) => serviceCheckbox.id === service.id);
    }

    getDisplayedPrice(service: Service): number {
        if (this.billingPeriod === 'yearly') {
            return this.parsePrice(service.annualPrice);
        }
        return this.parsePrice(service.price);
    }

    getServiceRecurrencyMonths(service: Service): number {
        const raw = Number(service.recurrency);
        return Number.isFinite(raw) && raw > 0 ? raw : 12;
    }

    goToPayment(): void {
        if (!this.servicesSelected.length) {
            return;
        }
        this.showStripeCheckout = true;
    }

    backToPlanSelection(): void {
        this.showStripeCheckout = false;
    }

    onPlanCheckoutCompleted(): void {
        this.changingPlan = false;
        this.showStripeCheckout = false;
        this.servicesSelected = [];
        this.showSuccessMessage('Abbonamento aggiornato');
        this.reload.emit();
        setTimeout(() => this.reload.emit(), 1500);
    }

    async retryPayment(): Promise<void> {
        if (!this.canRetryPayment || this.retryLoading) {
            return;
        }
        this.retryLoading = true;
        const result = await this.paymentService.retryCompanyPayment(this.companyId);
        this.retryLoading = false;
        if (!result) {
            this.showErrorMessage('Errore durante il tentativo di pagamento. Riprova.');
            return;
        }
        if (result.paid) {
            this.showSuccessMessage('Pagamento completato con successo.');
            this.reload.emit();
            return;
        }
        this.showInfoMessage(`Pagamento non completato. Stato: ${result.status || 'sconosciuto'}`);
        this.reload.emit();
    }

    isCompanySubscriptionYearly(): boolean {
        if (this.company.billedPeriod === 'yearly') {
            return true;
        }
        if (this.company.billedPeriod === 'monthly') {
            return false;
        }
        return this.company.service?.priceType !== 'Monthly';
    }

    getCompanySubscriptionDisplayPrice(): string {
        if (this.isCompanySubscriptionYearly()) {
            const annual = this.company.service?.annualPrice?.trim();
            if (annual) {
                return annual;
            }
        }
        return this.company.service?.price ?? '';
    }

    getCompanySubscriptionPeriodSuffix(): string {
        return this.isCompanySubscriptionYearly() ? '/anno' : '/mese';
    }

    getCompanySubscriptionBillingTitle(): string {
        return this.isCompanySubscriptionYearly() ? 'Piano annuale' : 'Canone mensile';
    }

    getNextInvoiceTotalCents(): number | null {
        const invoice = this.currentInvoice;
        if (!invoice) {
            return null;
        }
        const total = invoice.total ?? invoice.amount_due;
        return typeof total === 'number' && !Number.isNaN(total) ? total : null;
    }

    getNextInvoiceTotalEuros(): number | null {
        const cents = this.getNextInvoiceTotalCents();
        return cents == null ? null : cents / 100;
    }

    getNextInvoiceMonthlyCanoneEuros(): number | null {
        if (this.isCompanySubscriptionYearly()) {
            return null;
        }
        const price = this.company.service?.price?.trim();
        if (!price) {
            return null;
        }
        const amount = parseFloat(price.replace(',', '.'));
        return Number.isFinite(amount) ? amount : null;
    }

    getNextInvoiceEstimatedFeeEuros(): number | null {
        if (this.isCompanySubscriptionYearly()) {
            return null;
        }
        const total = this.getNextInvoiceTotalEuros();
        const canone = this.getNextInvoiceMonthlyCanoneEuros();
        if (total == null || canone == null) {
            return null;
        }
        const fee = total - canone;
        if (fee < -0.01) {
            return null;
        }
        return Math.max(0, fee);
    }

    private parsePrice(raw: string | undefined | null): number {
        const amount = parseFloat(String(raw ?? '').replace(/\s/g, '').replace(',', '.'));
        return Number.isNaN(amount) ? 0 : amount;
    }
}
